type TelegramBackButton = {
  show(): void;
  hide(): void;
  onClick(callback: () => void): void;
  offClick(callback: () => void): void;
};

type TelegramWebApp = {
  initData: string;
  colorScheme?: "light" | "dark";
  ready(): void;
  expand(): void;
  disableVerticalSwipes?: () => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  onEvent?: (event: "themeChanged", callback: () => void) => void;
  offEvent?: (event: "themeChanged", callback: () => void) => void;
  BackButton?: TelegramBackButton;
  openInvoice?: (url: string, callback?: (status: "paid" | "cancelled" | "failed" | "pending") => void) => void;
  openLink?: (url: string) => void;
  HapticFeedback?: {
    impactOccurred(style: "light" | "medium"): void;
    notificationOccurred(type: "error" | "success"): void;
  };
};

export type AppTheme = "light" | "dark";

export function telegramInitData(): string {
  return window.Telegram?.WebApp?.initData || window.__NURA_LOCAL_INIT_DATA__ || "";
}

export function resolveAppTheme(input: {
  telegramScheme?: AppTheme;
  hasTelegramSession: boolean;
  prefersDark: boolean;
}): AppTheme {
  if (input.hasTelegramSession && input.telegramScheme !== undefined) {
    return input.telegramScheme;
  }
  return input.prefersDark ? "dark" : "light";
}

export function telegramTheme(): AppTheme {
  const webApp = window.Telegram?.WebApp;
  const prefersDark = typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-color-scheme: dark)").matches
    : true;
  return resolveAppTheme({
    telegramScheme: webApp?.colorScheme,
    hasTelegramSession: (webApp?.initData ?? "").trim() !== "",
    prefersDark,
  });
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
    __NURA_LOCAL_INIT_DATA__?: string;
  }
}

export function connectTelegram(
  onBack: () => void,
  onThemeChange?: (theme: AppTheme) => void,
) {
  const webApp = window.Telegram?.WebApp;
  const systemTheme = typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-color-scheme: dark)")
    : undefined;

  function setChromeTheme(theme: AppTheme) {
    const color = theme === "dark" ? "#11100e" : "#fff8ea";
    webApp?.setHeaderColor?.(color);
    webApp?.setBackgroundColor?.(color);
  }

  function applyEnvironmentTheme() {
    const theme = telegramTheme();
    onThemeChange?.(theme);
    setChromeTheme(theme);
  }

  webApp?.ready();
  webApp?.expand();
  webApp?.disableVerticalSwipes?.();
  setChromeTheme(telegramTheme());
  webApp?.BackButton?.onClick(onBack);
  webApp?.onEvent?.("themeChanged", applyEnvironmentTheme);
  systemTheme?.addEventListener("change", applyEnvironmentTheme);

  return {
    setBackVisible(visible: boolean) {
      if (visible) webApp?.BackButton?.show();
      else webApp?.BackButton?.hide();
    },

    impact() {
      webApp?.HapticFeedback?.impactOccurred("light");
    },

    error() {
      webApp?.HapticFeedback?.notificationOccurred("error");
    },

    success() {
      webApp?.HapticFeedback?.notificationOccurred("success");
    },

    setTheme(theme: AppTheme) {
      setChromeTheme(theme);
    },

    openInvoice(url: string): Promise<"paid" | "cancelled" | "failed" | "pending"> {
      return new Promise((resolve) => {
        if (webApp?.openInvoice === undefined) {
          window.open(url, "_blank", "noopener,noreferrer");
          resolve("pending");
          return;
        }
        webApp.openInvoice(url, resolve);
      });
    },

    openExternal(url: string) {
      if (webApp?.openLink !== undefined) webApp.openLink(url);
      else window.open(url, "_blank", "noopener,noreferrer");
    },

    dispose() {
      webApp?.BackButton?.offClick(onBack);
      webApp?.offEvent?.("themeChanged", applyEnvironmentTheme);
      systemTheme?.removeEventListener("change", applyEnvironmentTheme);
    },
  };
}
